/** Memory settings section — view and delete AI-learned memories about the user. */
import { useState } from 'react';
import { Brain, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useMemories, useDeleteMemory } from '@/shared/api/hooks';
import { Button, Surface } from '@/interfaces/learner/ui';
import { SettingsSection } from './SettingsSection';

export function MemorySection({ delay = 0 }: { delay?: number }) {
  const { data: memories, isLoading } = useMemories();
  const deleteMutation = useDeleteMemory();
  const [confirmingId, setConfirmingId] = useState<string | null>(null);

  const handleDelete = (memoryId: string) => {
    if (confirmingId !== memoryId) {
      setConfirmingId(memoryId);
      return;
    }

    deleteMutation.mutate(memoryId, {
      onSuccess: () => {
        toast.success('Memory deleted');
        setConfirmingId(null);
      },
      onError: () => {
        toast.error('Failed to delete memory');
        setConfirmingId(null);
      },
    });
  };

  return (
    <SettingsSection title="Memory" delay={delay}>
      <p className="text-body-muted mb-4">
        Things the AI assistant remembers about you to personalise your experience. These are
        learned from your conversations.
      </p>

      {isLoading && <p className="text-body-muted">Loading...</p>}

      {!isLoading && (!memories || memories.length === 0) && (
        <Surface tone="muted" className="flex items-center gap-3 rounded-xl px-4 py-6">
          <Brain size={20} style={{ color: 'var(--color-text-muted)' }} />
          <p className="text-body-muted">
            No memories yet. The AI assistant will learn about you as you chat.
          </p>
        </Surface>
      )}

      {memories && memories.length > 0 && (
        <div className="flex flex-col gap-2">
          {memories.map((memory) => (
            <Surface
              key={memory.id}
              tone="muted"
              className="flex items-center justify-between gap-3 rounded-lg px-4 py-3"
            >
              <div className="flex-1 min-w-0">
                <p className="text-body" style={{ color: 'var(--color-text)' }}>
                  {memory.content}
                </p>
                {!memory.confirmed && <p className="text-meta-muted mt-0.5">Unconfirmed</p>}
              </div>
              <Button
                variant="ghost"
                size="icon"
                onPress={() => handleDelete(memory.id)}
                isDisabled={deleteMutation.isPending}
              >
                <Trash2
                  size={16}
                  style={{
                    color:
                      confirmingId === memory.id ? 'var(--color-error)' : 'var(--color-text-muted)',
                  }}
                />
              </Button>
            </Surface>
          ))}
        </div>
      )}
    </SettingsSection>
  );
}
